import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function L1PlotViewer({ selectedDate, selectedCampaign }) {
  const [selectedL1Plot, setSelectedL1Plot] = useState(null);
  const [l1PlotType, setL1PlotType] = useState(null); // Type of plot (html or png)
  const [plotType, setPlotType] = useState('signal_mie'); // Default to backscatter
  const [availablePlotTypes, setAvailablePlotTypes] = useState([]);
  const [integrationTime, setIntegrationTime] = useState(null); // Selected integration time (e.g. '20MIN' or 'default')
  const [availableIntegrationTimes, setAvailableIntegrationTimes] = useState([]);

  // Detect if user is on mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  // availablePlotTypes now comes with both value and label from the API

  // Fetch available plot types when date or campaign changes
  useEffect(() => {
    if (!selectedDate || !selectedCampaign) {
      setAvailablePlotTypes([]);
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    
    fetch(`${API_BASE}/api/quicklooks_l1/${selectedCampaign}/${formattedDate}`, {
      credentials: 'include' // Include session cookie
    })
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch available plot types'))
      .then(data => {
        console.log('Available plot types:', data.availablePlotTypes);
        setAvailablePlotTypes(data.availablePlotTypes);
        
        // If current plotType is not available, switch to the first available one
        if (data.availablePlotTypes.length > 0 && !data.availablePlotTypes.some(p => p.value === plotType)) {
          setPlotType(data.availablePlotTypes[0].value);
        }
      })
      .catch(err => {
        console.error('Error fetching available plot types:', err);
        setAvailablePlotTypes([]);
      });
  }, [selectedDate, selectedCampaign]);

  // Fetch the available integration times whenever date, campaign, or plot type changes
  useEffect(() => {
    if (!selectedDate || !selectedCampaign || !plotType) {
      setAvailableIntegrationTimes([]);
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');

    fetch(`${API_BASE}/api/quicklooks_l1-times/${selectedCampaign}/${formattedDate}/${plotType}`, {
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch available integration times'))
      .then(data => {
        setAvailableIntegrationTimes(data.times);
        setIntegrationTime(prev => (data.times.some(t => t.value === prev) ? prev : (data.times[0]?.value ?? null)));
      })
      .catch(err => {
        console.error('Error fetching available integration times:', err);
        setAvailableIntegrationTimes([]);
      });
  }, [selectedDate, selectedCampaign, plotType]);

  // Fetch the L1 quicklook plot for selected date, campaign, plot type, and integration time
  useEffect(() => {
    if (!selectedDate || !selectedCampaign) {
      setSelectedL1Plot(null);
      setL1PlotType(null);
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    const params = new URLSearchParams();
    if (isMobile()) params.set('mobile', 'true');
    if (integrationTime) params.set('integration', integrationTime);

    fetch(`${API_BASE}/api/quicklooks_l1/${selectedCampaign}/${formattedDate}/${plotType}?${params.toString()}`, {
      credentials: 'include' // Include session cookie
    })
      .then(res => res.ok ? res.json() : Promise.reject('Plot not found'))
      .then(data => {
        console.log('L1 quicklook data:', data);
        setSelectedL1Plot(`${API_BASE}${data.original}`);
        setL1PlotType(data.type);
      })
      .catch(err => {
        console.error('Error fetching L1 quicklook:', err);
        setSelectedL1Plot(null);
        setL1PlotType(null);
      });
  }, [selectedDate, selectedCampaign, plotType, availablePlotTypes, integrationTime]);

  return (
    <div className="content-card">
      <h2>L1 Quicklooks</h2>
      
      {(selectedCampaign === 'Test' || selectedCampaign === 'Kuehlungsborn' || selectedCampaign === 'Test_Andoya') && (
        <div className="warning-banner">
          ⚠️ Warning - currently with dummy lidar data!
        </div>
      )}
      
      <div>
        <div className="plot-controls">
          <h4 style={{ margin: 0 }}>
            L1 quicklook for {selectedCampaign} — {format(selectedDate, 'yyyy-MM-dd')}
          </h4>
          
          <select
            className="modern-select"
            value={plotType}
            onChange={(e) => setPlotType(e.target.value)}
            disabled={availablePlotTypes.length === 0}
          >
            {availablePlotTypes.length === 0 ? (
              <option value="">No L1 quicklook available</option>
            ) : (
              availablePlotTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))
            )}
          </select>

          {availableIntegrationTimes.length > 1 && (
            <select
              className="modern-select"
              value={integrationTime || ''}
              onChange={(e) => setIntegrationTime(e.target.value)}
            >
              {availableIntegrationTimes.map(time => (
                <option key={time.value} value={time.value}>
                  {time.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedL1Plot ? (
          <div>
            {l1PlotType === 'html' && (
              <p className="interactive-indicator">
                ✨ Interactive plot (zoomable)
              </p>
            )}
            <div className="plot-container" style={{ maxWidth: l1PlotType === 'html' ? '100%' : '1000px' }}>
              {l1PlotType === 'html' ? (
                <iframe 
                  src={selectedL1Plot} 
                  title={`${availablePlotTypes.find(p => p.value === plotType)?.label || plotType} L1 quicklook`}
                  className="interactive-plot-iframe-l1"
                />
              ) : (
                <img 
                  src={selectedL1Plot} 
                  alt={`${availablePlotTypes.find(p => p.value === plotType)?.label || plotType} L1 quicklook`}
                  style={{ width: '100%', height: 'auto', borderRadius: '8px' }} 
                />
              )}
            </div>
          </div>
        ) : (selectedDate && selectedCampaign) ? (
          availablePlotTypes.length === 0 ? (
            <div className="empty-state">
              <p>No L1 quicklooks available for this date and campaign.</p>
            </div>
          ) : (
            <div className="loading-state">
              <span className="loading-spinner"></span>
              Loading L1 quicklooks...
            </div>
          )
        ) : !selectedCampaign ? (
          <div className="empty-state">
            <p>Please select a campaign site.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default L1PlotViewer;